import React from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription } from '@agion/ui'
import { Link } from 'react-router'
import {
  stoppedSources,
  useOrgIngestionHealth,
  warningSources,
  derivedVisibilityDetail,
} from '@/hooks/useOrgIngestionHealth'
import { usePermissions } from '@/shared/auth/permission-context'
import type { IngestionHealthSource } from '@/types/ingestion-health.types'

/**
 * The administrator notice for governed ingestion (agion/Agion#2091).
 *
 * A source that stopped used to say so only on its own settings tab, so an
 * administrator learned about it by going to look. This reads the durable
 * health the worker persists, through the organization-wide governance read,
 * and says it where somebody already is.
 *
 * It is deliberately quiet. A failure the worker is still retrying raises
 * nothing, because a self-healing retry is not an administrator's problem. A
 * host that could not be read does raise something, because silence there is
 * indistinguishable from health, which is the defect this package exists to
 * remove.
 *
 * The failure code is not rendered here. The code vocabulary is open and this
 * banner is a summary; the code and its explanation live on the source's own
 * page, which the link leads to.
 */

const CAPSULE_LIST_PATH = '/intelligence/capsules'

function sourceName(source: IngestionHealthSource): string {
  return source.displayName || source.capsuleName || source.capsuleId
}

export const IngestionHealthBanner: React.FC = () => {
  const { t } = useTranslation(['pages'])
  // One reader for both shells, gated the way the next shell's rail dot is: a
  // member who cannot open the data sources page cannot act on the notice, so
  // the organization-wide fan-out is never fired on their behalf.
  //
  // A read that failed says nothing here. Claiming a fault nobody observed
  // would teach an administrator to ignore this banner.
  const { canNavigateTo, isLoading } = usePermissions()
  const { data: health } = useOrgIngestionHealth(!isLoading && canNavigateTo('agents'))

  if (!health) return null

  const stopped = stoppedSources(health)
  const warnings = warningSources(health)
  const unreadableHosts = health.unavailableHosts.length

  if (stopped.length === 0 && warnings.length === 0 && unreadableHosts === 0) return null

  const attended = [...stopped, ...warnings]
  const onlyAttended = attended.length === 1 ? attended[0] : undefined
  const linkTo = onlyAttended
    ? `${CAPSULE_LIST_PATH}/${onlyAttended.capsuleId}/manage?tab=settings`
    : CAPSULE_LIST_PATH

  const onlyStopped = stopped.length === 1 ? stopped[0] : undefined
  const onlyWarning = warnings.length === 1 ? warnings[0] : undefined
  const delayed = attended.filter((source) => source.delayedSurfaces?.length)

  let headline: string
  if (onlyStopped) {
    headline = t('pages:intelligence.ingestionHealth.oneStopped', {
      name: sourceName(onlyStopped),
      defaultValue: 'The data source {{name}} has stopped collecting data and needs attention.',
    })
  } else if (stopped.length > 1) {
    headline = t('pages:intelligence.ingestionHealth.manyStopped', {
      count: stopped.length,
      defaultValue: '{{count}} data sources have stopped collecting data and need attention.',
    })
  } else if (onlyWarning?.delayedSurfaces?.length) {
    headline = derivedVisibilityDetail(onlyWarning, t)
  } else if (onlyWarning) {
    headline = t('pages:intelligence.ingestionHealth.oneWarning', {
      name: sourceName(onlyWarning),
      defaultValue: 'The data source {{name}} is still collecting data, but something about it needs review.',
    })
  } else if (warnings.length > 1 && delayed.length > 0) {
    headline = t('pages:intelligence.ingestionHealth.updatesNeedReview', {
      count: warnings.length,
      defaultValue: 'Updates from {{count}} data sources need review.',
    })
  } else if (warnings.length > 1) {
    headline = t('pages:intelligence.ingestionHealth.manyWarnings', {
      count: warnings.length,
      defaultValue: '{{count}} data sources are still collecting data, but something about them needs review.',
    })
  } else {
    headline = unreadableHosts === 1
      ? t('pages:intelligence.ingestionHealth.oneHostUnreadable', {
        defaultValue:
          'Ingestion health could not be read on one host, so its data sources cannot be confirmed as up to date.',
      })
      : t('pages:intelligence.ingestionHealth.manyHostsUnreadable', {
        count: unreadableHosts,
        defaultValue:
          'Ingestion health could not be read on {{count}} hosts, so their data sources cannot be confirmed as up to date.',
      })
  }

  let hostNote: string | null = null
  if (unreadableHosts > 0 && attended.length > 0) {
    hostNote = unreadableHosts === 1
      ? t('pages:intelligence.ingestionHealth.alsoOneHostUnreadable', {
        defaultValue: 'Ingestion health could also not be read on one host.',
      })
      : t('pages:intelligence.ingestionHealth.alsoManyHostsUnreadable', {
        count: unreadableHosts,
        defaultValue: 'Ingestion health could also not be read on {{count}} hosts.',
      })
  }

  const linkLabel = onlyAttended
    ? t('pages:intelligence.ingestionHealth.reviewSource', 'Review this data source')
    : t('pages:intelligence.ingestionHealth.reviewSources', 'Review data sources')

  return (
    <div className="px-6 pt-4">
      <Alert
        variant={stopped.length > 0 ? 'error' : 'warning'}
        className="[&>svg+div]:translate-y-0 [&>svg]:top-3.5"
      >
        <AlertDescription>
          {headline}
          {delayed.filter((source) => stopped.length > 0 || source !== onlyWarning).map((source) => (
            <span key={`${source.hostId}:${source.capsuleId}`}>
              {' '}
              {derivedVisibilityDetail(source, t)}
            </span>
          ))}
          {hostNote ? ` ${hostNote}` : null}
          {' '}
          <Link to={linkTo} className="underline underline-offset-2 hover:opacity-80">
            {linkLabel}
          </Link>
        </AlertDescription>
      </Alert>
    </div>
  )
}
